/**
 * Admin → Orbit feed. Sends each matter to Orbit (the firm's relationship platform) once its
 * New Matter Memo is complete, with numbers following when they are assigned. Off until an
 * admin turns it on here. What is sent is fixed in code (functions/src/orbit/orbitEvent.js):
 * client name, contact, city and state, practice group, area of law, the attorneys, and
 * numbers once they exist. Never the matter name, parties, conflicts, fees, or walled matters.
 *
 * Reads through the admin callables, not Firestore: the outbox is functions-only.
 */

import { useEffect, useState } from 'react';
import { CallStatus, useCallable } from '../../lib/useCallable';

const STATUS_LABELS = {
  queued: 'Waiting to send',
  sending: 'Sending',
  delivered: 'Delivered',
  rejected: 'Refused by Orbit',
  failed: 'Gave up after 7 days',
  superseded: 'Replaced by a newer copy',
};

const when = (iso) =>
  iso
    ? new Intl.DateTimeFormat('en-US', {
        dateStyle: 'medium',
        timeStyle: 'short',
        timeZone: 'America/Los_Angeles',
      }).format(new Date(iso))
    : '';

const button =
  'rounded bg-firm-700 dark:bg-firm-500 px-4 py-2 text-sm font-medium text-white hover:bg-firm-900 dark:hover:bg-firm-400 disabled:opacity-50';
const secondary =
  'rounded border border-slate-300 dark:border-slate-600 px-3 py-1.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50';

export default function AdminOrbitFeed() {
  const { run, busy, error, saved } = useCallable();
  const [view, setView] = useState(null);
  const [backfill, setBackfill] = useState(null);
  const [confirmOn, setConfirmOn] = useState(false);

  useEffect(() => {
    run('adminGetOrbitFeed', {}).then((r) => r && setView(r));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!view) return <p className="text-sm text-slate-500 dark:text-slate-400">{error || 'Loading…'}</p>;

  const enabled = view.settings.enabled;
  const toggle = async (next) => {
    const r = await run('adminUpdateOrbitFeed', { enabled: next });
    if (r) setView(r);
    setConfirmOn(false);
  };
  const sendExisting = async () => {
    const r = await run('adminOrbitBackfill', {});
    if (r) {
      setView(r);
      setBackfill(r.counts);
    }
  };
  const retry = async (id) => {
    const r = await run('adminRetryOrbitOutbox', { id });
    if (r) setView(r);
  };
  const refresh = async () => {
    const r = await run('adminGetOrbitFeed', {});
    if (r) setView(r);
  };

  return (
    <div className="space-y-4">
      <section className="rounded-lg bg-white dark:bg-slate-900 p-4 shadow space-y-3">
        <div className="flex items-center gap-3">
          <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Orbit feed</h2>
          <span
            className={`rounded px-2 py-0.5 text-xs font-medium ${
              enabled
                ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-100'
                : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200'
            }`}
          >
            {enabled ? 'On' : 'Off'}
          </span>
          <CallStatus busy={busy} error={error} saved={saved} />
        </div>
        <p className="text-sm text-slate-600 dark:text-slate-300">
          When on, each matter is sent to Orbit once its New Matter Memo is complete. Client and
          matter numbers follow when they are assigned. A matter that is reopened, or given a
          Wall Builder designation, is taken back out of Orbit.
        </p>
        <div className="grid gap-3 sm:grid-cols-2 text-sm">
          <div>
            <h3 className="font-medium text-slate-800 dark:text-slate-100">Sent</h3>
            <p className="text-slate-600 dark:text-slate-300">
              Client name, contact name and title, invoice email, city and state; practice
              group and area of law; new client or new matter; the responsible partner and
              working attorneys (firm email, employee code, origination split); client and
              matter numbers once assigned.
            </p>
          </div>
          <div>
            <h3 className="font-medium text-slate-800 dark:text-slate-100">Never sent</h3>
            <p className="text-slate-600 dark:text-slate-300">
              The matter name and description, parties and opposing counsel, conflicts, prior
              representation, fees and retainer, insurance, client source, engagement letter,
              litigation hold, attachments, street address, zip, phone, and any walled matter.
            </p>
          </div>
        </div>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Sent to <code>{view.endpoint}</code> with a Google-signed token for this app's service
          account. Orbit accepts it only from service accounts on its own allowlist, and only
          while its intake feed is on.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          {enabled ? (
            <button type="button" className={secondary} disabled={busy} onClick={() => toggle(false)}>
              Turn off
            </button>
          ) : confirmOn ? (
            <>
              <span className="text-sm text-slate-700 dark:text-slate-200">
                Start sending completed matters to Orbit?
              </span>
              <button type="button" className={button} disabled={busy} onClick={() => toggle(true)}>
                Yes, turn on
              </button>
              <button type="button" className={secondary} onClick={() => setConfirmOn(false)}>
                Cancel
              </button>
            </>
          ) : (
            <button type="button" className={button} disabled={busy} onClick={() => setConfirmOn(true)}>
              Turn on
            </button>
          )}
          {enabled && (
            <button type="button" className={secondary} disabled={busy} onClick={sendExisting}>
              Send existing matters
            </button>
          )}
          <button type="button" className={secondary} disabled={busy} onClick={refresh}>
            Refresh
          </button>
        </div>
        {backfill && (
          <p role="status" className="text-sm text-slate-700 dark:text-slate-200">
            {backfill.queued} queued, {backfill.unchanged} already in Orbit, {backfill.notSent} not
            sendable (walled, or no firm attorney).
          </p>
        )}
      </section>

      <section className="rounded-lg bg-white dark:bg-slate-900 p-4 shadow space-y-3">
        <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Delivery</h2>
        <ul className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-700 dark:text-slate-200">
          {Object.entries(STATUS_LABELS).map(([k, label]) => (
            <li key={k}>
              {label}: <strong>{view.counts[k] || 0}</strong>
            </li>
          ))}
        </ul>
        {view.recent.length === 0 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">Nothing sent yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="text-xs text-slate-500 dark:text-slate-400">
                <tr>
                  <th className="py-1 pr-3 font-medium">Queued</th>
                  <th className="py-1 pr-3 font-medium">Matter</th>
                  <th className="py-1 pr-3 font-medium">Event</th>
                  <th className="py-1 pr-3 font-medium">Status</th>
                  <th className="py-1 font-medium" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {view.recent.map((r) => (
                  <tr key={r.id} className="align-top text-slate-700 dark:text-slate-200">
                    <td className="py-1.5 pr-3 whitespace-nowrap">{when(r.createdAt)}</td>
                    <td className="py-1.5 pr-3">
                      <a className="text-firm-700 dark:text-firm-100 underline" href={`/matters/${r.matterId}`}>
                        Open matter
                      </a>
                    </td>
                    <td className="py-1.5 pr-3">
                      {r.type === 'matter.withdrawn'
                        ? 'Taken out of Orbit'
                        : r.hasNumbers
                          ? 'Sent with numbers'
                          : 'Sent, numbers pending'}
                      {r.source === 'backfill' && <span className="text-slate-500"> (existing)</span>}
                    </td>
                    <td className="py-1.5 pr-3">
                      {STATUS_LABELS[r.status] || r.status}
                      {r.lastStatus && r.status !== 'delivered' && (
                        <span className="text-slate-500"> (Orbit answered {r.lastStatus})</span>
                      )}
                      {r.problems.length > 0 && (
                        <ul className="mt-1 list-disc pl-4 text-xs text-red-700 dark:text-red-300">
                          {r.problems.map((p) => (
                            <li key={p}>{p}</li>
                          ))}
                        </ul>
                      )}
                    </td>
                    <td className="py-1.5">
                      {r.status === 'failed' && (
                        <button type="button" className={secondary} disabled={busy} onClick={() => retry(r.id)}>
                          Retry
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
